/** Schedule parameter form — renders one control per parameter, ordered by array position. */

import { Box, TextField, FormControl, InputLabel, MenuItem, Select, Switch, FormControlLabel, Autocomplete, Chip, Typography } from "@mui/material";
import type { ParameterDef } from "@/types";
import type { ScheduleParameterValues, DateRangeParamValue } from "@/types/schedule";
import DateRangeParamControl from "@/components/schedules/DateRangeParamControl";
import SearchAutocomplete, { SEARCH_PARAM_ENDPOINTS } from "@/components/schedules/SearchAutocomplete";
import GDrivePicker from "@/components/GDrivePicker";

export interface ScheduleParameterFormProps {
  parameters: ParameterDef[];
  values: ScheduleParameterValues;
  onChange: (values: ScheduleParameterValues) => void;
  errors: Record<string, string>;
  pluginId?: string;
}

/** Determine if a paramType is a search-type parameter. */

function isSearchType(paramType: string): boolean {
  return paramType in SEARCH_PARAM_ENDPOINTS || paramType.endsWith("_search");
}

export default function ScheduleParameterForm({ parameters, values, onChange, errors, pluginId }: ScheduleParameterFormProps) {
  const updateValue = (paramName: string, value: unknown) => {
    onChange({
      ...values,
      [paramName]: {
        ...values[paramName],
        value,
        mode: values[paramName]?.mode ?? "static",
      },
    });
  };

  const updateDateRange = (paramName: string, dateValue: DateRangeParamValue) => {
    if (dateValue.mode === "dynamic") {
      onChange({
        ...values,
        [paramName]: {
          value: null,
          mode: "dynamic",
          offset: dateValue.offset,
        },
      });
    } else {
      onChange({
        ...values,
        [paramName]: {
          value: dateValue.value,
          mode: "static",
        },
      });
    }
  };

  const renderControl = (param: ParameterDef) => {
    const { name, label, type, required, choices, help_text, min, max } = param;
    const paramType = `param_${type}`;
    const entry = values[name];
    const error = errors[name];

    switch (paramType) {
      case "param_text":
        return (
          <TextField
            key={name}
            label={label}
            value={(entry?.value as string) ?? ""}
            onChange={(e) => updateValue(name, e.target.value)}
            required={required}
            helperText={error || help_text}
            error={!!error}
            size="small"
            fullWidth
          />
        );

      case "param_prompt":
        return (
          <TextField
            key={name}
            label={label}
            value={(entry?.value as string) ?? ""}
            onChange={(e) => updateValue(name, e.target.value)}
            required={required}
            helperText={error || help_text}
            error={!!error}
            size="small"
            fullWidth
            multiline
            minRows={3}
          />
        );

      case "param_date_range": {
        const dateValue: DateRangeParamValue = entry?.mode === "dynamic"
          ? { mode: "dynamic", offset: entry.offset ?? { amount: 7, unit: "days" } }
          : { mode: "static", value: (entry?.value as { from: string; to: string }) ?? { from: "", to: "" } };

        return (
          <DateRangeParamControl
            key={name}
            paramName={name}
            label={label}
            helpText={help_text}
            value={dateValue}
            onChange={(v) => updateDateRange(name, v)}
            error={error}
          />
        );
      }

      case "param_select":
      case "param_model_select":
      case "param_asset_type_select": {
        const hasChoices = choices && choices.length > 0;
        return (
          <FormControl key={name} fullWidth size="small" required={required} error={!!error} disabled={!hasChoices}>
            <InputLabel>{label}</InputLabel>
            <Select
              value={(entry?.value as string) ?? ""}
              onChange={(e) => updateValue(name, e.target.value)}
              label={label}
            >
              {hasChoices ? (
                choices.map((c) => (
                  <MenuItem key={c.value} value={c.value}>
                    {c.label}
                  </MenuItem>
                ))
              ) : null}
            </Select>
            {(error || help_text || !hasChoices) && (
              <Typography variant="caption" color={error ? "error" : "text.secondary"} sx={{ mt: 0.5, ml: 1.5 }}>
                {error || (!hasChoices ? "No options available" : help_text)}
              </Typography>
            )}
          </FormControl>
        );
      }

      case "param_multi_select": {
        const hasChoices = choices && choices.length > 0;
        const selectedValues = (entry?.value as string[]) ?? [];
        const selectedOptions = choices?.filter((c) => selectedValues.includes(c.value)) ?? [];
        return (
          <Autocomplete
            key={name}
            multiple
            options={choices ?? []}
            getOptionLabel={(opt) => opt.label}
            value={selectedOptions}
            onChange={(_, selected) => updateValue(name, selected.map((s) => s.value))}
            disabled={!hasChoices}
            isOptionEqualToValue={(a, b) => a.value === b.value}
            renderTags={(tagValues, getTagProps) =>
              tagValues.map((option, index) => (
                <Chip {...getTagProps({ index })} key={option.value} label={option.label} size="small" />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={label}
                required={required}
                helperText={error || (!hasChoices ? "No options available" : help_text)}
                error={!!error}
                size="small"
              />
            )}
          />
        );
      }

      case "param_boolean":
        return (
          <Box key={name}>
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(entry?.value)}
                  onChange={(e) => updateValue(name, e.target.checked)}
                />
              }
              label={label}
            />
            {(error || help_text) && (
              <Typography variant="caption" color={error ? "error" : "text.secondary"} sx={{ display: "block", ml: 1.5 }}>
                {error || help_text}
              </Typography>
            )}
          </Box>
        );

      case "param_integer":
        return (
          <TextField
            key={name}
            label={label}
            type="number"
            value={entry?.value !== undefined && entry?.value !== null ? String(entry.value) : ""}
            onChange={(e) => {
              const raw = e.target.value;
              if (raw === "") {
                updateValue(name, null);
              } else {
                const num = parseInt(raw, 10);
                if (!isNaN(num)) {
                  updateValue(name, num);
                }
              }
            }}
            required={required}
            helperText={error || help_text}
            error={!!error}
            size="small"
            fullWidth
            slotProps={{
              htmlInput: {
                min: min ?? undefined,
                max: max ?? undefined,
                step: 1,
              },
            }}
          />
        );

      case "param_gdrive_file_picker":
        return (
          <GDrivePicker
            key={name}
            label={label}
            helperText={error || help_text}
            mode="file"
            value={(entry?.value as Parameters<typeof GDrivePicker>[0]["value"]) ?? null}
            onChange={(v) => updateValue(name, v)}
            showRange={param.show_range}
            mimeFilter={param.mime_filter}
          />
        );

      case "param_gdrive_folder_picker":
        return (
          <GDrivePicker
            key={name}
            label={label}
            helperText={error || help_text}
            mode="folder"
            value={(entry?.value as Parameters<typeof GDrivePicker>[0]["value"]) ?? null}
            onChange={(v) => updateValue(name, v)}
            showTemplatePicker={param.show_template_picker}
            templateType={param.template_type}
          />
        );

      default:
        // Handle search-type parameters
        if (isSearchType(paramType)) {
          const endpoint = SEARCH_PARAM_ENDPOINTS[paramType] ?? `/reporting/entities/search?type=${type.replace("_search", "")}`;
          return (
            <SearchAutocomplete
              key={name}
              paramName={name}
              label={label}
              helpText={help_text}
              searchEndpoint={endpoint}
              value={(entry?.value as { id: string; name: string } | null) ?? null}
              onChange={(v) => updateValue(name, v)}
              required={required}
              error={error}
              credential={param.credential}
              pluginId={pluginId}
            />
          );
        }

        // Fallback for unknown types: render as text field
        return (
          <TextField
            key={name}
            label={label}
            value={(entry?.value as string) ?? ""}
            onChange={(e) => updateValue(name, e.target.value)}
            required={required}
            helperText={error || help_text}
            error={!!error}
            size="small"
            fullWidth
          />
        );
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {parameters.map(renderControl)}
    </Box>
  );
}
